import { useEffect, useRef, useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { jsonStore } from "@/manager/cache/localdb";
import { knowledgeListAgentUserFolders } from "@/services/knowledge-list-agent-user-folders";
import { knowledgeAddAgentUserKnowledgeFoldersByToken } from "@/services/knowledge-add-agent-user-knowledge-folders-by-token";
import { removeAgentUserKnowledgeFolders } from "@/services/knowledge-delete-agent-user-knowledge-folders-by-token";
import { useTranslation } from "react-i18next";

type KnowledgeType = "" | "video" | "podcast" | "moments" | "post" | "article";

type FolderItem = {
  id: number;
  folderName: string;
  createTime: string;
  updateTime?: string;
  userId?: number;
  folderId: number;
  agentId?: number;
  itemCount?: number;
  type?: number;
};

type ChangeEventDetail = { folders: FolderItem[] };
type ChangeEvent = CustomEvent<ChangeEventDetail>;

class AgentUserKnowledgeEvents {
  constructor(private eventName: string) { }
  listen(listener: (e: ChangeEvent) => void): () => void {
    window.addEventListener(this.eventName, listener as EventListener);
    return () => {
      window.removeEventListener(this.eventName, listener as EventListener);
    };
  }
  dispatch(detail: ChangeEventDetail): void {
    const event = new CustomEvent<ChangeEventDetail>(this.eventName, { detail });
    window.dispatchEvent(event);
  }
}

class AgentUserKnowledgeLocal {
  constructor(private storageKey: string) { }
  async get(): Promise<FolderItem[] | null> {
    return await jsonStore.get<FolderItem[]>(this.storageKey);
  }
  async set(folders: FolderItem[]): Promise<void> {
    await jsonStore.set(this.storageKey, folders);
  }
  async clear(): Promise<void> {
    await jsonStore.set(this.storageKey, null);
  }
}

class AgentUserKnowledgeRemote {
  constructor(private cache: AgentUserKnowledgeLocal, private agentId?: string, private type?: KnowledgeType) { }
  async getCached() {
    return await this.cache.get();
  }
  async fetch(): Promise<FolderItem[]> {
    const data = await knowledgeListAgentUserFolders({ agentId: this.agentId || "", type: this.type || "" });
    const folders = data.map(item => ({
      id: item.id,
      folderName: item.folderName,
      createTime: item.createTime,
      updateTime: item.updateTime,
      userId: item.userId,
      folderId: item.folderId,
      agentId: item.agentId,
      itemCount: item.itemCount,
      type: item.type,
    }));
    await this.cache.set(folders);
    return folders;
  }
  async updateFolders(folderIds: number[]) {
    return await knowledgeAddAgentUserKnowledgeFoldersByToken({
      folderIds,
      agentId: this.agentId || "",
      type: this.type || "",
    });
  }
  async removeFolders(folderIds: number[]) {
    return await removeAgentUserKnowledgeFolders({
      folderIds,
      agentId: this.agentId || "",
      type: this.type || "",
    });
  }
}

export class AgentUserKnowledgeManager {
  private readonly STORAGE_PREFIX = "MIUTA_AGENT_USER_KNOWLEDGE";
  private folders: FolderItem[] = [];
  private cache: AgentUserKnowledgeLocal;
  private api: AgentUserKnowledgeRemote;
  private events: AgentUserKnowledgeEvents;

  constructor(agentId?: string, type?: KnowledgeType) {
    const key = `${this.STORAGE_PREFIX}:${agentId || "all"}:${type || "all"}`;
    this.cache = new AgentUserKnowledgeLocal(key);
    this.api = new AgentUserKnowledgeRemote(this.cache, agentId, type);
    this.events = new AgentUserKnowledgeEvents(key);
  }

  getFolders(): FolderItem[] {
    return this.folders;
  }

  private setFolders(folders: FolderItem[]) {
    this.folders = folders;
    this.dispatch();
  }

  async init(): Promise<void> {
    try {
      const cached = await this.api.getCached();
      if (cached) {
        this.setFolders(cached);
        this.fetch();
        return;
      }
    } catch (error) {
      console.log(error);
      throw error;
    }
    await this.fetch();
  }

  async fetch(): Promise<void> {
    const folders = await this.api.fetch();
    this.setFolders(folders);
  }

  async updateFolders(folderIds: number[]) {
    try {
      await this.api.updateFolders(folderIds);
    } catch (error) {
      console.error("Agent user materials update failed:", error.message);
      throw error;
    }
  }

  async removeFolders(folderIds: number[]) {
    try {
      await this.api.removeFolders(folderIds);
    } catch (error) {
      console.error("Agent user materials remove failed:", error.message);
      throw error;
    }
  }

  reset(): void {
    this.setFolders([]);
    this.clear();
  }

  clear(): void {
    this.cache.clear();
  }

  listen(listener: (e: ChangeEvent) => void): () => void {
    return this.events.listen(listener);
  }

  private dispatch(): void {
    this.events.dispatch({ folders: this.folders });
  }
}

export const agentUserKnowledgeHooks = {
  useFolderListData(props: { agentId?: string; type?: KnowledgeType }) {
    const { agentId, type } = props;
    const { showToastByCode, toast } = useToast();
    const { t } = useTranslation();

    const managerRef = useRef<AgentUserKnowledgeManager | null>(null);

    const [folders, setFolders] = useState<FolderItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [updateLoading, setUpdateLoading] = useState(false);

    useEffect(() => {
      managerRef.current = new AgentUserKnowledgeManager(agentId, type);
      setFolders(managerRef.current.getFolders());
      const unsubscribe = managerRef.current.listen((e) => setFolders(e.detail.folders));
      return unsubscribe;
    }, [agentId, type]);

    useEffect(() => {
      const run = async () => {
        if (!managerRef.current) return;
        setLoading(true);
        try {
          await managerRef.current.init();
        } catch (error) {
          showToastByCode(error);
        } finally {
          setLoading(false);
        }
      };
      run();
    }, [agentId, type]);

    const refresh = async () => {
      if (!managerRef.current) return;
      setLoading(true);
      try {
        await managerRef.current.fetch();
      } catch (error) {
        showToastByCode(error);
      } finally {
        setLoading(false);
      }
    };

    // 添加
    const addKnowledgeFolders = async (folderIds: number[]) => {
      setUpdateLoading(true);
      try {
        await managerRef.current.updateFolders(folderIds);
        await managerRef.current.fetch();
        toast({
          description: t('agent_creation.subscription_settings.add_remove_modal.add_success'),
        })
      } catch (error) {
        showToastByCode(error);
      } finally {
        setUpdateLoading(false);
      }
    };

    // 删除
    const removeKnowledgeFolders = async (folderIds: number[]) => {
      setUpdateLoading(true);
      try {
        await managerRef.current.removeFolders(folderIds);
        await managerRef.current.fetch();
        toast({
          description: t('agent_creation.subscription_settings.add_remove_modal.remove_success'),
        })
      } catch (error) {
        showToastByCode(error);
      } finally {
        setUpdateLoading(false);
      }
    };

    // 更新
    const updateKnowledge = async (params: { removeIds: number[]; addIds: number[] }) => {
      setUpdateLoading(true);
      try {
        if (params.removeIds.length) {
          await managerRef.current.removeFolders(params.removeIds);
          if (!params.addIds.length) {
            await managerRef.current.fetch();
          }
        }
        if (params.addIds.length) {
          await managerRef.current.updateFolders(params.addIds);
          await managerRef.current.fetch();
        }
      } catch (error) {
        showToastByCode(error);
      } finally {
        setUpdateLoading(false);
      }
    };

    return { folders, loading, refresh, addKnowledgeFolders, removeKnowledgeFolders, updateLoading, updateKnowledge };
  },
};

export default AgentUserKnowledgeManager;
